/**
 * dataConverters.ts
 * -----------------------------------------------------------------------------
 * Converts context API SphereNode data to NodeData format for orbital visualization.
 * Also supports legacy Sphere conversion for locally-added spheres.
 */

import type { Sphere, SatelliteSphere } from 'types/global';
import type { SphereNode } from 'types/context';
import { NodeData } from '../../molecules/OrbitalVisualization/constants';
import { getGlyphIconDataUri } from '../../../utils/avatarIcons';

/**
 * Convert a SphereNode from the context API directly to NodeData for visualization.
 * Reads children as satellites without an intermediate Sphere conversion.
 */
export function convertSphereNodeToNodeData(node: SphereNode): NodeData {
  // Extract velocity from points or status
  const velocity = node.points?.velocity?.score
    ?? node.status?.currentValue?.velocity?.score
    ?? 0;

  // Extract gravity from points
  const gravity = node.points?.gravity?.score ?? 0;
  const impact = node.points?.gravity?.impact ?? 0;

  // Generate label from name
  const name = (node.properties?.name as string) ?? 'SP';
  const label = name.substring(0, 2).toUpperCase();

  // Map children directly to SatelliteSphere[]
  const satellites: SatelliteSphere[] = (node.children || []).map(child => ({
    id: child.id,
    name: (child.properties?.name as string) || child.label || 'Sphere',
    status: child.status?.currentValue?.status || 'unknown',
    gravity: child.points?.gravity?.score ?? 0,
    glyph: child.glyph,
    glyphColor: child.glyphColor,
  }));

  const satelliteCount = satellites.length;
  const labelBackground = gravity >= 70;

  // The glyph icon; ArmillaryView replaces it with the sphere's picture once its read finds one.
  const imagePath = getGlyphIconDataUri(node.glyph, node.glyphColor);

  // Extract urgency from edge properties
  const urgencyRaw = node.edge?.properties?.urgency;
  const urgency = typeof urgencyRaw === 'number' ? urgencyRaw : 3;

  return {
    id: node.id,
    label,
    customLabel: name,
    imagePath,
    labelBackground,
    satelliteCount,
    satellites,
    velocity,
    gravity,
    urgency,
    impact,
    sphereNode: node,
  };
}

/**
 * Convert an array of SphereNodes to NodeData array.
 */
export function convertSphereNodesToNodeData(nodes: SphereNode[]): NodeData[] {
  return nodes.map(convertSphereNodeToNodeData);
}

/**
 * Convert a legacy Sphere to NodeData (for locally-added spheres).
 */
export function convertSphereToNodeData(sphere: Sphere): NodeData {
  const urgencyStr = sphere.connectingPerspectives?.[0]?.urgency;
  const urgency = urgencyStr ? Number.parseInt(urgencyStr, 10) : 3;
  const velocity = sphere.status?.currentValue?.velocity?.score ?? 0;
  const gravity = sphere.gravity?.score ?? 50;
  const impact = sphere.gravity?.impact ?? 0;
  const name = sphere.properties?.name ?? 'SP';
  const label = name.substring(0, 2).toUpperCase();
  const satellites = sphere.satellites || [];
  const satelliteCount = satellites.length > 0 ? satellites.length : Math.min(5, Math.floor(gravity / 25));
  const labelBackground = gravity >= 70;
  const imagePath = getGlyphIconDataUri(sphere.glyph, sphere.glyphColor);

  return {
    id: sphere.id,
    label,
    customLabel: sphere.properties?.name,
    imagePath,
    labelBackground,
    satelliteCount,
    satellites,
    velocity,
    gravity,
    urgency: Number.isNaN(urgency) ? 3 : urgency,
    impact,
    sphereData: sphere,
  };
}

/**
 * Convert an array of legacy Spheres to NodeData array.
 */
export function convertSpheresToNodeData(spheres: Sphere[]): NodeData[] {
  return spheres.map(convertSphereToNodeData);
}

/**
 * Get a human-readable gravity bucket label.
 */
export function getGravityBucketLabel(gravity: number): string {
  if (gravity >= 80) return 'Critical';
  if (gravity >= 60) return 'High';
  if (gravity >= 40) return 'Medium';
  if (gravity >= 20) return 'Low';
  return 'Minimal';
}

/**
 * Get a color for a gravity bucket (for UI indicators).
 */
export function getGravityBucketColor(gravity: number): string {
  if (gravity >= 80) return '#FF4136';
  if (gravity >= 60) return '#FF851B';
  if (gravity >= 40) return '#FFDC00';
  if (gravity >= 20) return '#2ECC40';
  return '#AAAAAA';
}

/**
 * Get a velocity direction indicator.
 */
export function getVelocityDirection(velocity: number): 'improving' | 'declining' | 'stable' {
  if (velocity > 10) return 'improving';
  if (velocity < -10) return 'declining';
  return 'stable';
}

/**
 * Get a human-readable velocity description.
 */
export function getVelocityDescription(velocity: number): string {
  const absVelocity = Math.abs(velocity);
  const direction = velocity >= 0 ? 'improving' : 'declining';
  if (absVelocity >= 70) return `Rapidly ${direction}`;
  if (absVelocity >= 40) return `Steadily ${direction}`;
  if (absVelocity >= 10) return `Slowly ${direction}`;
  return 'Stable';
}
